import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { context } from "utils/Provider";
import {
  useMatcherTableDataLazyQuery,
  useQueryProcessedTableMatcherTableNameLazyQuery,
  useMergeProcessedMutation,
  useQueryProcessedViewsDataLazyQuery,
} from "graphql/types/graphql";
import { enqueueSnackbar } from "notistack";

interface TableRow {
  FX_UNIQUE_ID: string;
  [key: string]: unknown;
}

interface TableColumn {
  Header: string;
  accessor: string;
}

interface TableData<T> {
  columns: TableColumn[];
  rows: T[];
}

interface EdgeData<T> {
  edges: T[];
}

function transformToTable<T extends Record<string, unknown>>(data: EdgeData<T> | undefined): TableData<T> {
  if (!data?.edges || data.edges.length === 0) {
    return { columns: [], rows: [] };
  }

  const columns = Object.keys(data.edges[0]).map((key) => ({
    Header: key,
    accessor: key,
  }));

  return {
    columns,
    rows: data.edges,
  };
}

interface UseManualMergeProps {
  tableName: string;
  identifier: string;
  pageSize: number;
  currentPage: number;
  mainTableData: TableData<TableRow>;
  setTableData: (data: TableRow[]) => void;
  setTotalCount: (count: number) => void;
  setSelectedRows: (rows: string[]) => void;
  searchTableData: TableRow[];
  hasSearched: boolean;
}

interface UseManualMergeReturn {
  matcherTableName: string | null;
  fetchedIdentifier: string | null;
  mainPageSize: number;
  mainCurrentPage: number;
  matcherPageSize: number;
  matcherCurrentPage: number;
  selectedLeftRowFXID: string | null;
  selectedRightRow: string | null;
  filteredMainTableData: TableData<TableRow>;
  unmatchedTotalCount: number;
  matcherTableTransformed: TableData<TableRow>;
  matcherTableLoading: boolean;
  matcherDataLoading: boolean;
  matcherTableTotalCount: number;
  mergeLoading: boolean;
  handleSelectLeftRow: (fxUniqueId: string) => void;
  handleSelectRightRow: (cruxyId: string) => void;
  handleMerge: () => Promise<void>;
  setMainPageSize: (size: number) => void;
  setMainCurrentPage: (page: number) => void;
  setMatcherPageSize: (size: number) => void;
  setMatcherCurrentPage: (page: number) => void;
}

export const useManualMerge = ({
  tableName,
  identifier,
  pageSize,
  currentPage,
  mainTableData,
  setTableData,
  setTotalCount,
  setSelectedRows,
  searchTableData,
  hasSearched,
}: UseManualMergeProps): UseManualMergeReturn => {
  const { setIsLoading, selectedCube } = useContext(context);
  const [matcherTableName, setMatcherTableName] = useState<string | null>(null);
  const [fetchedIdentifier, setFetchedIdentifier] = useState<string | null>(null);
  const [mainPageSize, setMainPageSize] = useState(pageSize);
  const [mainCurrentPage, setMainCurrentPage] = useState(0);
  const [matcherPageSize, setMatcherPageSize] = useState(pageSize);
  const [matcherCurrentPage, setMatcherCurrentPage] = useState(0);
  const [selectedLeftRowFXID, setSelectedLeftRowFXID] = useState<string | null>(null);
  const [selectedRightRow, setSelectedRightRow] = useState<string | null>(null);
  const [unmatchedTotalCount, setUnmatchedTotalCount] = useState(0);

  const [getMatcherTableName, { data: matcherTableDataResponse, loading: matcherTableLoading }] =
    useQueryProcessedTableMatcherTableNameLazyQuery({
      fetchPolicy: "cache-and-network",
    });
  const [getMatcherTableData, { data: matcherTableData, loading: matcherDataLoading }] = useMatcherTableDataLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  const [getUnmatchedData, { data: unmatchedData, loading: unmatchedLoading, error: unmatchedError }] =
    useQueryProcessedViewsDataLazyQuery({
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: false,
    });

  useEffect(() => {
    if (selectedCube.cubeId && tableName && identifier) {
      getUnmatchedData({
        variables: {
          cubeId: selectedCube.cubeId,
          filter: { viewName: tableName },
          limit: mainPageSize,
          offset: mainCurrentPage * mainPageSize,
          identifier,
          includeMatched: false,
        },
      });
    }
  }, [selectedCube.cubeId, tableName, identifier, mainPageSize, mainCurrentPage, getUnmatchedData]);

  const filteredMainTableData = useMemo(() => {
    return transformToTable(unmatchedData?.queryProcessedViewsData);
  }, [unmatchedData]);

  useEffect(() => {
    if (unmatchedData?.queryProcessedViewsData?.totalCount !== undefined) {
      setUnmatchedTotalCount(unmatchedData.queryProcessedViewsData.totalCount);
    } else {
      setUnmatchedTotalCount(0);
    }
  }, [unmatchedData]);

  const matcherTableTransformed = useMemo(
    () => transformToTable(matcherTableData?.matcherTableData),
    [matcherTableData]
  );

  const matcherTableTotalCount = useMemo(() => matcherTableData?.matcherTableData?.totalCount || 0, [matcherTableData]);

  useEffect(() => {
    if (tableName) {
      getMatcherTableName({ variables: { tableName } });
    }
  }, [tableName, getMatcherTableName]);

  useEffect(() => {
    if (matcherTableDataResponse?.queryProcessedTableMatcherTableName?.matcherTable) {
      const fetchedMatcherTableName = matcherTableDataResponse.queryProcessedTableMatcherTableName.matcherTable;
      const identifierFromResponse = matcherTableDataResponse.queryProcessedTableMatcherTableName.identifier;
      setMatcherTableName(fetchedMatcherTableName);
      setFetchedIdentifier(identifierFromResponse || null);
      getMatcherTableData({
        variables: {
          tableName: fetchedMatcherTableName,
          cubeId: selectedCube.cubeId,
          limit: matcherPageSize,
          offset: matcherCurrentPage * matcherPageSize,
        },
      });
    }
  }, [matcherTableDataResponse, matcherCurrentPage, matcherPageSize, getMatcherTableData, selectedCube.cubeId]);

  const [mergeProcessed, { loading: mergeLoading }] = useMergeProcessedMutation();

  const handleSelectLeftRow = useCallback((fxUniqueId: string) => {
    setSelectedLeftRowFXID((prev) => (prev === fxUniqueId ? null : fxUniqueId));
  }, []);

  const handleSelectRightRow = useCallback((id: string) => {
    setSelectedRightRow((prev) => (prev === id ? null : id));
  }, []);

  const handleMerge = useCallback(async () => {
    if (!selectedLeftRowFXID || !selectedRightRow || !matcherTableName || !fetchedIdentifier) {
      return;
    }

    const leftRow = filteredMainTableData.rows.find((row) => row.FX_UNIQUE_ID.toString() === selectedLeftRowFXID);

    const rightRow = hasSearched
      ? searchTableData.find((row, index) =>
          row.FX_UNIQUE_ID ? row.FX_UNIQUE_ID === selectedRightRow : index.toString() === selectedRightRow
        )
      : matcherTableTransformed.rows.find((row, index) =>
          row.FX_UNIQUE_ID ? row.FX_UNIQUE_ID === selectedRightRow : index.toString() === selectedRightRow
        );

    if (!leftRow || !rightRow) {
      enqueueSnackbar(`Merge failed - leftRow or rightRow not found: Left-${leftRow}---Right-${selectedRightRow}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await mergeProcessed({
        variables: {
          tableName,
          identifier,
          fxId: selectedLeftRowFXID,
          value: rightRow[identifier].toString(),
          score: 1,
        },
        update: (cache) => {
          cache.evict({ fieldName: "queryProcessedViewsData" });

          const updatedRows = filteredMainTableData.rows.filter((row) => row.FX_UNIQUE_ID !== selectedLeftRowFXID);
          setTableData(updatedRows);
          setTotalCount(unmatchedTotalCount - 1);
          setSelectedRows([]);
        },
      });

      if (response.data?.mergeProcessed?.success) {
        enqueueSnackbar("Rows merged successfully!", { variant: "success" });
        setSelectedLeftRowFXID(null);
        setSelectedRightRow(null);
      } else {
        enqueueSnackbar(response.data?.mergeProcessed?.error || "Merge failed", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to merge rows", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchedIdentifier,
    selectedLeftRowFXID,
    selectedRightRow,
    matcherTableName,
    filteredMainTableData.rows,
    hasSearched,
    searchTableData,
    matcherTableTransformed.rows,
    setIsLoading,
    mergeProcessed,
    tableName,
    identifier,
    unmatchedTotalCount,
    setTableData,
    setTotalCount,
    setSelectedRows,
  ]);

  return {
    matcherTableName,
    fetchedIdentifier,
    mainPageSize,
    mainCurrentPage,
    matcherPageSize,
    matcherCurrentPage,
    selectedLeftRowFXID,
    selectedRightRow,
    filteredMainTableData,
    unmatchedTotalCount,
    matcherTableTransformed,
    matcherTableLoading,
    matcherDataLoading: matcherDataLoading || unmatchedLoading,
    matcherTableTotalCount,
    mergeLoading,
    handleSelectLeftRow,
    handleSelectRightRow,
    handleMerge,
    setMainPageSize,
    setMainCurrentPage,
    setMatcherPageSize,
    setMatcherCurrentPage,
  };
};
